import { WorkProduct, Feature, Initiative } from '../types/index';
import { WORK_PRODUCT_LIBRARY } from '../constants/index';

const WORK_PRODUCT_PHASE_ALIASES: Record<string, string> = {
  discovery: 'discovery',
  specify: 'discovery',
  design: 'design',
  plan: 'design',
  'task-breakdown': 'task-breakdown',
  'task breakdown': 'task-breakdown',
  task_breakdown: 'task-breakdown',
  code: 'code',
  implement: 'code',
  implementation: 'code',
  qa: 'qa',
  validate: 'qa',
  validation: 'qa',
  deploy: 'deploy'
};

const normalizeWorkProductPhase = (phase: unknown): string | null => {
  const key = String(phase || '').trim().toLowerCase();
  return WORK_PRODUCT_PHASE_ALIASES[key] || null;
};

// Generate unique ID
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Map API response to WorkProduct interface
export const mapApiToWorkProducts = (apiData: Record<string, any[]>): Record<string, WorkProduct[]> => {
  const workProductsByPhase: Record<string, WorkProduct[]> = {
    discovery: [],
    design: [],
    "task-breakdown": [],
    code: [],
    qa: [],
    deploy: []
  };

  Object.keys(apiData).forEach(apiPhase => {
    // The SDD catalog returns canonical stages (Specify, Plan, Task Breakdown,
    // Implementation, Validation), while the existing UI still groups cards in
    // its legacy phase buckets. Normalize at this API boundary so generated
    // documents are not silently discarded.
    const phase = normalizeWorkProductPhase(apiPhase);
    if (phase && workProductsByPhase.hasOwnProperty(phase)) {
      const mapped = apiData[apiPhase]
        .filter(wp => wp.name !== 'Missing Title' && wp.id !== null) // Filter out placeholder items
        .map(wp => ({
          // BaseEntity fields
          id: wp.id ? wp.id.toString() : generateId(),
          createdAt: wp.createdAt || new Date().toISOString(), // Convert to ISO string for Redux serialization
          lastModified: wp.lastModified || new Date().toISOString(), // Convert to ISO string for Redux serialization

          // WorkProduct required fields
          name: wp.name || 'Untitled',
          type: wp.type || wp.content_type || 'document',  // Use type field, fallback to content_type
          phase: normalizeWorkProductPhase(wp.phase) || phase,
          description: wp.description || '',
          shortdescription: wp.shortdescription || '',
          status: (wp.status === 'available' || wp.status === 'disabled') ? 'select' : 'active' as "select" | "active",
          state: (wp.state === 'disabled' ? 'available' : wp.state) as 'available' | 'generating' | 'editable',
          approved: wp.approved || false,
          hasContent: wp.hasContent || false,
          content: wp.content,
          attachments: wp.attachments || [],

          // Relationship fields
          children: wp.children || [],
          parent: wp.parent,
          isChild: wp.isChild || false,

          // Optional fields
          owner: wp.owner,
          timestamp: wp.timestamp,
          activatedAt: wp.activatedAt,

          // Store original content_type separately if needed
          content_type: wp.content_type,
          artifact_path: wp.artifact_path || wp.artifact,
          output_format: wp.output_format,

          // User story and level tracking fields
          feature_id: wp.feature_id,
          user_story_id: wp.user_story_id,
          variable: wp.variable,
          level: wp.level,
          display_level: wp.display_level,
          scope_level: wp.scope_level,

          // Rebuild indicator
          needs_rebuild: wp.needs_rebuild || false
        }));
      workProductsByPhase[phase].push(...mapped);
    }
  });

  return workProductsByPhase;
};

// Get phase color based on phase ID
export const getPhaseColor = (phaseId: string): string => {
  const colors: Record<string, string> = {
    discovery: '#1976D2',
    design: '#7B1FA2',
    "task-breakdown": '#94B2FF',
    code: '#388E3C',
    qa: '#F57C00',
    deploy: '#D32F2F'
  };
  return colors[phaseId] || '#757575';
};

// Initialize work products by phase from the library or API data
export const initializeWorkProductsByPhase = (apiData?: Record<string, any[]>): Record<string, WorkProduct[]> => {
  // If API data is provided, use it
  if (apiData) {
    return mapApiToWorkProducts(apiData);
  }

  // Return empty structure - don't use hardcoded data to prevent flash of wrong content
  const workProductsByPhase: Record<string, WorkProduct[]> = {
    discovery: [],
    design: [],
    "task-breakdown": [],
    code: [],
    qa: [],
    deploy: []
  };

  // Commented out to prevent flash of hardcoded data
  // The API will load the actual data
  /*
  // Load discovery work products
  if (WORK_PRODUCT_LIBRARY.discovery && WORK_PRODUCT_LIBRARY.discovery.length > 0) {
    workProductsByPhase.discovery = WORK_PRODUCT_LIBRARY.discovery.map((wp: any) => ({
      id: wp.id ? wp.id.toString() : generateId(),
      name: wp.name,
      type: wp.type,
      phase: wp.phase,
      description: wp.description,
      state: wp.state as 'available-to-add' | 'editable',
      approved: wp.approved,
      children: [], // Always empty in single-level mode
      hasContent: false,
      isChild: false // All work products are top-level
    }));
  }

  // Load design work products
  if (WORK_PRODUCT_LIBRARY.design && WORK_PRODUCT_LIBRARY.design.length > 0) {
    workProductsByPhase.design = WORK_PRODUCT_LIBRARY.design.map((wp: any) => ({
      id: wp.id ? wp.id.toString() : generateId(),
      name: wp.name,
      type: wp.type,
      phase: wp.phase,
      description: wp.description,
      state: wp.state as 'available-to-add' | 'editable',
      approved: wp.approved,
      children: [], // Always empty in single-level mode
      hasContent: false,
      isChild: false // All work products are top-level
    }));
  }

  */
  
  return workProductsByPhase;
};

// Get default matrix (feature) for auto-selection
export const getDefaultMatrix = (initiatives: Initiative[], lastUsedMatrixId?: string | null): Feature | null => {
  if (!initiatives.length) return null;

  // Try to find last used matrix first
  if (lastUsedMatrixId) {
    for (const initiative of initiatives) {
      const feature = initiative.features.find(f => f.id === lastUsedMatrixId);
      if (feature) return feature;
    }
  }

  // Fallback to first feature of first initiative
  const firstInitiative = initiatives[0];
  return firstInitiative.features.length > 0 ? firstInitiative.features[0] : null;
};

// Helper to find work product by ID across all phases - simplified for single level
export const findWorkProductById = (workProductsByPhase: Record<string, WorkProduct[]>, workProductId: string): WorkProduct | null => {
  for (const phase of Object.keys(workProductsByPhase)) {
    const workProduct = workProductsByPhase[phase].find(wp => wp.id === workProductId);
    if (workProduct) return workProduct;
  }
  return null;
};

// Helper to get work product display list for rendering - simplified for single level
export const getWorkProductDisplayList = (workProductsByPhase: Record<string, WorkProduct[]>, phase: string): WorkProduct[] => {
  const phaseWorkProducts = workProductsByPhase[phase] || [];
  // Return all editable work products (no hierarchy filtering needed)
  return phaseWorkProducts.filter(wp => wp.state === 'editable');
};

// Helper to count active work products - simplified for single level
export const getActiveWorkProductCount = (workProductsByPhase: Record<string, WorkProduct[]>, phase: string): number => {
  const phaseWorkProducts = workProductsByPhase[phase] || [];
  return phaseWorkProducts.filter(wp => wp.state === 'editable').length;
};

// Helper to count approved work products - simplified for single level
export const getApprovedWorkProductCount = (workProductsByPhase: Record<string, WorkProduct[]>, phase: string): number => {
  const phaseWorkProducts = workProductsByPhase[phase] || [];
  return phaseWorkProducts.filter(wp => wp.state === 'editable' && wp.approved).length;
};

// Helper to get select work products (available to add)
export const getSelectWorkProducts = (workProductsByPhase: Record<string, WorkProduct[]>, phase: string): WorkProduct[] => {
  const phaseWorkProducts = workProductsByPhase[phase] || [];
  return phaseWorkProducts.filter(wp => wp.state === 'available-to-add');
};

// Helper to get active work products (editable, all at same level)
export const getActiveWorkProducts = (workProductsByPhase: Record<string, WorkProduct[]>, phase: string): WorkProduct[] => {
  const phaseWorkProducts = workProductsByPhase[phase] || [];
  return phaseWorkProducts.filter(wp => wp.state === 'editable');
};

// Helper to validate work product structure - simplified for single level
export const validateWorkProductStructure = (workProductsByPhase: Record<string, WorkProduct[]>): boolean => {
  // In single-level mode, all work products should have no children and isChild = false
  for (const phase of Object.keys(workProductsByPhase)) {
    for (const workProduct of workProductsByPhase[phase]) {
      if (workProduct.children && workProduct.children.length > 0) {
        console.warn('Work product has children in single-level mode:', workProduct.id);
        return false;
      }
      if (workProduct.isChild === true) {
        console.warn('Work product marked as child in single-level mode:', workProduct.id);
        return false;
      }
    }
  }
  return true;
};

// DEPRECATED: These functions are no longer needed in single-level mode but kept for compatibility

// Helper to find parent work product of a child (deprecated in single-level mode)
export const findParentWorkProduct = (workProductsByPhase: Record<string, WorkProduct[]>, childWorkProduct: WorkProduct): WorkProduct | null => {
  // Always returns null in single-level mode since there are no parent-child relationships
  return null;
};

// Helper to get all child work products of a parent (deprecated in single-level mode)
export const getChildWorkProducts = (workProductsByPhase: Record<string, WorkProduct[]>, parentId: string): WorkProduct[] => {
  // Always returns empty array in single-level mode since there are no children
  return [];
};

// Helper to check if a work product has expandable children (deprecated in single-level mode)
export const hasExpandableChildren = (workProduct: WorkProduct): boolean => {
  // Always returns false in single-level mode since no work products have children
  return false;
};

// Helper to get expanded child work products for a parent (deprecated in single-level mode)
export const getExpandedChildren = (workProduct: WorkProduct, expandedWorkProducts: Set<string>): WorkProduct[] => {
  // Always returns empty array in single-level mode since there are no children
  return [];
};

// Helper to validate work product hierarchy (deprecated in single-level mode)
export const validateWorkProductHierarchy = (workProductsByPhase: Record<string, WorkProduct[]>): boolean => {
  // Always returns true in single-level mode since there's no hierarchy to validate
  return true;
};

// Helper to get work product display hierarchy for rendering (deprecated, use getWorkProductDisplayList instead)
export const getWorkProductDisplayHierarchy = (workProductsByPhase: Record<string, WorkProduct[]>, phase: string): WorkProduct[] => {
  // Redirect to new single-level function
  return getWorkProductDisplayList(workProductsByPhase, phase);
};

// Helper to check if a work product is derived from a parent with children (deprecated in single-level mode)
export const isDerivedFromParentWithChildren = (workProduct: WorkProduct): boolean => {
  // Always returns false in single-level mode since there are no derived children
  return false;
};

// Helper to get the original parent work product name from a derived child (deprecated in single-level mode)
export const getOriginalParentName = (childWorkProduct: WorkProduct): string | null => {
  // Always returns null in single-level mode since there are no parent-child relationships
  return null;
};
